/**
 * Карточка номенклатуры.
 *
 * Всё о позиции в одном окне, разложенное по вкладкам: основное,
 * фото, состав, модификаторы. Раньше из строки меню правились только
 * имя и цена, а за составом надо было идти в «Калькуляцию», за фото —
 * в витрину. Человек, заводящий новое блюдо, обходил три экрана
 * и половину забывал.
 *
 * Форма меняется от ТИПА позиции: у сырья нет цены и его нет в меню,
 * у услуги нет остатка, у товара нет техкарты. Показывать всё всем —
 * значит собрать пятнадцать полей, из которых половина не к месту,
 * и получить муку в меню и услугу с остатком минус три.
 */
import { useState } from 'react';
import {
  PRODUCT_KINDS, formatMoney, kindFields, kindFromFlags,
  type ProductKind,
} from '@dastarhan/core';

const API = '/api/v1';

export type ProductDraft = {
  id?: string;
  name: string;
  categoryId: string | null;
  kind: ProductKind;
  price: string;
  unit: string;
  sku?: string | null;
  barcode?: string | null;
  minStock?: string;
  isActive: boolean;
  trackStock: boolean;
  isPurchased: boolean;
  isIngredient: boolean;
  photoUrl?: string | null;
  description?: string | null;
  badges?: string | null;
  abv?: string;
};

/*
 * Четыре единицы, а не десяток.
 *
 * Граммы и миллилитры отдельными единицами кажутся удобством, но
 * порождают путаницу: часть техкарт в граммах, часть в килограммах,
 * и остаток «1200» читается как полтора кило или как грамм с четвертью.
 * Держим килограммы и литры, дробные значения пишем через запятую —
 * 0,125 кг понятнее, чем 125 г в соседстве с 2 кг.
 */
const UNITS = [
  { key: 'PORTION', label: 'Порция' },
  { key: 'PIECE', label: 'Штука' },
  { key: 'KG', label: 'Килограмм' },
  { key: 'L', label: 'Литр' },
];

type Tab = 'main' | 'photo' | 'recipe' | 'mods';

export function ProductDialog({
  token, initial, categories, onClose, onSaved,
}: {
  token: string;
  initial: ProductDraft;
  categories: { id: string; name: string; parentId?: string | null }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [tab, setTab] = useState<Tab>('main');
  const [d, setD] = useState<ProductDraft>(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const f = kindFields(d.kind);
  const set = (patch: Partial<ProductDraft>) => setD((p) => ({ ...p, ...patch }));

  /**
   * Смена типа переставляет признаки сама.
   *
   * Человек выбирает «Сырьё» и ждёт, что позиция уйдёт со склада
   * в кладовку, а не что ему придётся вспоминать про три галочки
   * внизу формы.
   */
  const setKind = (kind: ProductKind) => {
    const info = PRODUCT_KINDS.find((k) => k.key === kind)!;
    set({
      kind,
      trackStock: info.onStock,
      isPurchased: info.purchased,
      isIngredient: kind === 'RAW' || kind === 'PREP',
      ...(info.inMenu ? {} : { price: '0' }),
    });
  };

  const save = async () => {
    if (d.name.trim().length < 2) { setErr('Название слишком короткое'); return; }
    setSaving(true); setErr('');
    try {
      const body = {
        name: d.name.trim(),
        categoryId: d.categoryId,
        price: Math.round(Number(d.price.replace(',', '.') || '0') * 100),
        unit: d.unit,
        sku: d.sku?.trim() || null,
        barcode: d.barcode?.trim() || null,
        minStock: d.minStock?.trim() ? Number(d.minStock.replace(',', '.')) : null,
        isActive: d.isActive,
        trackStock: d.trackStock,
        isPurchased: d.isPurchased,
        isIngredient: d.isIngredient,
        photoUrl: d.photoUrl?.trim() || null,
        description: d.description?.trim() || null,
        badges: d.badges?.trim() || null,
        abv: d.abv?.trim() ? Number(d.abv.replace(',', '.')) : null,
      };
      const r = await fetch(
        d.id ? `${API}/menu/products/${d.id}` : `${API}/menu/products`,
        {
          method: d.id ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        },
      );
      if (!r.ok) { setErr('Не удалось сохранить'); return; }
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dialog" onClick={onClose}>
      <div className="dialog-card product-dialog" onClick={(e) => e.stopPropagation()}>
        <header className="pd-head">
          <div>
            <span className="eyebrow">Номенклатура</span>
            <h2>{d.id ? d.name || 'Позиция' : 'Новая позиция'}</h2>
          </div>
          <button className="pd-x" onClick={onClose} aria-label="Закрыть">×</button>
        </header>

        <div className="pd-tabs">
          <button className={tab === 'main' ? 'on' : ''} onClick={() => setTab('main')}>
            Основные данные
          </button>
          {f.photo && (
            <button className={tab === 'photo' ? 'on' : ''} onClick={() => setTab('photo')}>
              Витрина
            </button>
          )}
          {f.recipe && d.id && (
            <button className={tab === 'recipe' ? 'on' : ''} onClick={() => setTab('recipe')}>
              Состав
            </button>
          )}
          {f.inMenu && d.id && (
            <button className={tab === 'mods' ? 'on' : ''} onClick={() => setTab('mods')}>
              Модификаторы
            </button>
          )}
        </div>

        {tab === 'main' && (
          <div className="pd-body">
            <label>Наименование
              <input autoFocus value={d.name}
                onChange={(e) => set({ name: e.target.value })} />
            </label>

            {/* Тип первым после названия: от него зависит, какие поля
                вообще появятся ниже */}
            <label>Тип позиции
              <select value={d.kind} onChange={(e) => setKind(e.target.value as ProductKind)}>
                {PRODUCT_KINDS.map((k) => (
                  <option key={k.key} value={k.key}>{k.label}</option>
                ))}
              </select>
              <i className="split">
                {PRODUCT_KINDS.find((k) => k.key === d.kind)?.hint}
              </i>
            </label>

            {f.inMenu && (
              <label>Категория
                <select value={d.categoryId ?? ''}
                  onChange={(e) => set({ categoryId: e.target.value || null })}>
                  <option value="">— без категории —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.parentId ? '   ' : ''}{c.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="pd-row">
              <label>Единица измерения
                <select value={d.unit} onChange={(e) => set({ unit: e.target.value })}>
                  {UNITS.map((u) => (
                    <option key={u.key} value={u.key}>{u.label}</option>
                  ))}
                </select>
              </label>

              {f.price && (
                <label>Цена, ₸
                  <input inputMode="decimal" value={d.price}
                    onChange={(e) => set({ price: e.target.value })} />
                </label>
              )}
            </div>

            {f.stock && (
              <div className="pd-row">
                <label>Минимальный остаток
                  <input inputMode="decimal" value={d.minStock ?? ''}
                    placeholder="0"
                    onChange={(e) => set({ minStock: e.target.value })} />
                  <i className="split">
                    Ниже него позиция попадёт в «пора заказать». Пусто —
                    не следим
                  </i>
                </label>
                <label>Артикул
                  <input value={d.sku ?? ''} placeholder="необязательно"
                    onChange={(e) => set({ sku: e.target.value })} />
                </label>
              </div>
            )}

            {f.stock && (
              <label>Штрихкод
                <input value={d.barcode ?? ''} placeholder="сканируется на приёмке"
                  onChange={(e) => set({ barcode: e.target.value })} />
              </label>
            )}

            {f.inMenu && (
              <label>Крепость, % об.
                <input inputMode="decimal" value={d.abv ?? ''} placeholder="для алкоголя"
                  onChange={(e) => set({ abv: e.target.value })} />
                <i className="split">Пусто — обычная позиция</i>
              </label>
            )}

            <div className="pd-checks">
              <label className="check">
                <input type="checkbox" checked={d.isActive}
                  onChange={(e) => set({ isActive: e.target.checked })} />
                <span>
                  <b>В работе</b><br />
                  <small>Выключено — позиция скрыта везде, история цела</small>
                </span>
              </label>

              {f.stock && (
                <label className="check">
                  <input type="checkbox" checked={d.trackStock}
                    onChange={(e) => set({ trackStock: e.target.checked })} />
                  <span>
                    <b>Вести остаток</b><br />
                    <small>Считаем на складе, показываем в пересчёте</small>
                  </span>
                </label>
              )}

              {f.stock && (
                <label className="check">
                  <input type="checkbox" checked={d.isPurchased}
                    onChange={(e) => set({ isPurchased: e.target.checked })} />
                  <span>
                    <b>Закупается</b><br />
                    <small>Выключено у того, что делаем сами: тесто, соус</small>
                  </span>
                </label>
              )}
            </div>
          </div>
        )}

        {tab === 'photo' && (
          <div className="pd-body">
            <p className="hint">
              Это видит гость в электронном меню. Пустые поля просто
              не показываются — заполнять всё не обязательно.
            </p>
            <label>Ссылка на фото
              <input value={d.photoUrl ?? ''} placeholder="https://…"
                onChange={(e) => set({ photoUrl: e.target.value })} />
            </label>
            {d.photoUrl && (
              <img className="pd-photo" src={d.photoUrl} alt=""
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            )}
            <label>Описание
              <textarea rows={3} value={d.description ?? ''}
                placeholder="состав, граммовка — две строки мелким шрифтом"
                onChange={(e) => set({ description: e.target.value })} />
            </label>
            <label>Отметки
              <input value={d.badges ?? ''} placeholder="🌶️ острое · 🥜 орехи"
                onChange={(e) => set({ badges: e.target.value })} />
              <i className="split">
                Аллергены и особенности — гость решает по ним, брать или нет
              </i>
            </label>
          </div>
        )}

        {tab === 'recipe' && (
          <div className="pd-body">
            <p className="hint">
              Состав задаётся в разделе «Калькуляция» — там видно
              себестоимость и наценку по всем блюдам разом, а не по одному.
            </p>
            <a className="btn" href="#calc" onClick={onClose}>
              Перейти в калькуляцию
            </a>
          </div>
        )}

        {tab === 'mods' && (
          <div className="pd-body">
            <p className="hint">
              Модификаторы — общие группы: «Степень прожарки», «Соус».
              Одна группа цепляется к десятку блюд, поэтому живут они
              отдельно.
            </p>
            <a className="btn" href="#mods" onClick={onClose}>
              Перейти к модификаторам
            </a>
          </div>
        )}

        {err && <p className="rb-warn">{err}</p>}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Отмена</button>
          <button className="btn primary" disabled={saving} onClick={() => void save()}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Собрать черновик из строки меню — или пустой для новой позиции. */
export function draftFrom(p?: {
  id: string; name: string; categoryId?: string | null; price: number;
  unit: string; isActive: boolean;
  /* Остальное необязательно: список меню отдаёт не всё, а карточка
     дозаполняет пустыми значениями — это честнее, чем требовать
     от списка тащить пятнадцать полей ради одного окна */
  sku?: string | null; barcode?: string | null;
  minStock?: number | null; trackStock?: boolean; hasRecipe?: boolean;
  isPurchased?: boolean; isIngredient?: boolean;
  photoUrl?: string | null; description?: string | null;
  badges?: string | null; abv?: number | null;
}, categoryId?: string | null): ProductDraft {
  if (!p) {
    return {
      name: '', categoryId: categoryId ?? null, kind: 'DISH',
      price: '', unit: 'PORTION', isActive: true,
      trackStock: true, isPurchased: false, isIngredient: false,
    };
  }
  return {
    id: p.id,
    name: p.name,
    categoryId: p.categoryId ?? null,
    kind: kindFromFlags({
      isIngredient: p.isIngredient,
      trackStock: p.trackStock ?? true,
      price: p.price,
      hasRecipe: p.hasRecipe,
    }),
    price: p.price ? String(p.price / 100) : '',
    unit: p.unit,
    sku: p.sku ?? '',
    barcode: p.barcode ?? '',
    minStock: p.minStock != null ? String(p.minStock) : '',
    isActive: p.isActive,
    trackStock: p.trackStock ?? true,
    isPurchased: p.isPurchased ?? true,
    isIngredient: p.isIngredient ?? false,
    photoUrl: p.photoUrl ?? '',
    description: p.description ?? '',
    badges: p.badges ?? '',
    abv: p.abv != null ? String(p.abv) : '',
  };
}
